import { alpha, createTheme } from '@mui/material/styles';
import type { CSSProperties } from 'react';

// PassportColors are the surfaces and lines of the passport page and the passport card.
interface PassportColors {
  card: string;
  photo: string;
  photoBorder: string;
  line: string;
  control: string;
  dashed: string;
  body: string;
  hint: string;
  qrGround: string;
  shadow: string;
}

// IgTokens are the Night gallery colour tokens of ADR-0069, one set per mode.
export interface IgTokens {
  ground: string;
  raised: string;
  raisedStrong: string;
  rule: string;
  text: string;
  textSecondary: string;
  textMuted: string;
  accent: string;
  link: string;
  footer: string;
}

declare module '@mui/material/styles' {
  interface Palette {
    accent: Palette['primary'];
    passport: PassportColors;
    ig: IgTokens;
  }

  interface PaletteOptions {
    accent?: PaletteOptions['primary'];
    passport?: PassportColors;
    ig?: IgTokens;
  }

  interface TypographyVariants {
    mono: CSSProperties;
    eyebrow: CSSProperties;
    label: CSSProperties;
  }

  interface TypographyVariantsOptions {
    mono?: CSSProperties;
    eyebrow?: CSSProperties;
    label?: CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    mono: true;
    eyebrow: true;
    label: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    accent: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    accent: true;
  }
}

export type ThemeMode = 'light' | 'dark';

const tokens: Record<ThemeMode, IgTokens> = {
  dark: {
    ground: '#0E0D0B',
    raised: '#12110E',
    raisedStrong: '#1A1815',
    rule: '#2A2723',
    text: '#EDE8DF',
    textSecondary: '#CFC9BE',
    textMuted: '#A39E94',
    accent: '#C9A75A',
    link: '#D4B26A',
    footer: '#0A0908',
  },
  light: {
    ground: '#F6F3EE',
    raised: '#EFEBE4',
    raisedStrong: '#EAE5DC',
    rule: '#DDD6CB',
    text: '#161513',
    textSecondary: '#3A3732',
    textMuted: '#5E5A53',
    accent: '#8A6A24',
    link: '#7A5C1C',
    footer: '#EFEBE4',
  },
};

// modePalette holds what differs between the two modes besides the ADR-0069 tokens.
const modePalette = {
  dark: {
    primary: { light: '#D4B26A', dark: '#A98A3F', contrastText: '#0E0D0B' },
    secondary: { main: '#be2136', light: '#d44d5e', dark: '#8b1726', contrastText: '#ffffff' },
    accent: { main: '#cd03ff', light: '#dc4dff', dark: '#9900c2', contrastText: '#ffffff' },
    disabled: '#6A665D',
    error: { main: '#f44336', light: '#e57373', dark: '#d32f2f' },
    warning: { main: '#ffa726', light: '#ffb74d', dark: '#f57c00' },
    success: { main: '#66bb6a', light: '#81c784', dark: '#388e3c' },
    info: { main: '#29b6f6', light: '#4fc3f7', dark: '#0288d1' },
    passport: {
      card: '#141414',
      photo: '#2a2a2a',
      photoBorder: '#3a3a3a',
      line: '#262626',
      control: '#4a4a4a',
      dashed: '#5a5a5a',
      body: '#d6d6d6',
      hint: '#8c8c8c',
      qrGround: '#ffffff',
      shadow: 'rgba(0, 0, 0, 0.6)',
    },
  },
  light: {
    primary: { light: '#A3822F', dark: '#6B5119', contrastText: '#ffffff' },
    secondary: { main: '#a51c2e', light: '#be2136', dark: '#7a1421', contrastText: '#ffffff' },
    accent: { main: '#8f00b3', light: '#a82bc8', dark: '#6a0086', contrastText: '#ffffff' },
    disabled: '#8C877D',
    error: { main: '#b3261e', light: '#d4574f', dark: '#8c1d17' },
    warning: { main: '#8a5a00', light: '#b37a12', dark: '#6b4500' },
    success: { main: '#2e6b33', light: '#4a8a50', dark: '#1f4d23' },
    info: { main: '#0b5e8c', light: '#2f82b3', dark: '#084868' },
    passport: {
      card: '#EFEBE4',
      photo: '#E4DED3',
      photoBorder: '#CFC7BA',
      line: '#DDD6CB',
      control: '#8F887C',
      dashed: '#A39E94',
      body: '#3A3732',
      hint: '#5E5A53',
      qrGround: '#ffffff',
      shadow: 'rgba(22, 21, 19, 0.15)',
    },
  },
} as const;

const SERIF = '"Cormorant Garamond", serif';
export const fontFamilyMono = '"IBM Plex Mono", "Roboto Mono", monospace';

// buildTheme builds the Night gallery MUI theme for one mode.
export function buildTheme(mode: ThemeMode) {
  const ig = tokens[mode];
  const m = modePalette[mode];
  const gold = alpha(ig.accent, 0.15);

  return createTheme({
    palette: {
      mode,
      primary: { main: ig.accent, ...m.primary },
      secondary: m.secondary,
      accent: m.accent,
      background: { default: ig.ground, paper: ig.raised },
      text: {
        primary: ig.text,
        secondary: ig.textSecondary,
        disabled: m.disabled,
      },
      divider: ig.rule,
      passport: m.passport,
      ig,
      error: m.error,
      warning: m.warning,
      success: m.success,
      info: m.info,
    },
    shape: {
      borderRadius: 8,
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontFamily: SERIF, fontWeight: 500, letterSpacing: '-0.02em' },
      h2: { fontFamily: SERIF, fontWeight: 500, letterSpacing: '-0.01em' },
      h3: { fontFamily: SERIF, fontWeight: 500 },
      h4: { fontFamily: SERIF, fontWeight: 500 },
      h5: { fontWeight: 500 },
      h6: { fontWeight: 500 },
      button: { fontWeight: 600, textTransform: 'none' },
      overline: { fontWeight: 600, letterSpacing: '0.1em' },
      mono: {
        fontFamily: fontFamilyMono,
        fontWeight: 500,
      },
      eyebrow: {
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: 1.5,
        textTransform: 'uppercase',
        letterSpacing: '0.22em',
        color: ig.accent,
      },
      label: {
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: 1.5,
        textTransform: 'uppercase',
        letterSpacing: '0.2em',
        color: ig.textMuted,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarColor: `${ig.rule} ${ig.ground}`,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '10px 24px',
            // MUI 9 removed the containedPrimary-style class keys; the same rules as variants.
            variants: [
              // A large button is at least a 48px touch target.
              {
                props: { size: 'large' },
                style: { minHeight: 48 },
              },
              {
                props: { variant: 'contained', color: 'primary' },
                style: {
                  color: m.primary.contrastText,
                  '&:hover': {
                    backgroundColor: ig.link,
                  },
                },
              },
              {
                props: { variant: 'contained', color: 'secondary' },
                style: {
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: m.secondary.light,
                  },
                },
              },
              {
                props: { variant: 'outlined', color: 'primary' },
                style: {
                  borderColor: ig.accent,
                  '&:hover': {
                    borderColor: ig.link,
                    backgroundColor: alpha(ig.accent, 0.08),
                  },
                },
              },
            ],
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: ig.ground,
            borderBottom: `1px solid ${ig.rule}`,
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: ig.raised,
            border: `1px solid ${ig.rule}`,
            transition: 'border-color 0.2s ease-in-out',
            '&:hover': {
              borderColor: alpha(ig.accent, 0.4),
            },
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: ig.rule,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: ig.accent,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
          },
          colorPrimary: {
            backgroundColor: gold,
            color: ig.accent,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: ig.rule,
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: ig.link,
            textDecorationColor: alpha(ig.link, 0.4),
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: ig.raisedStrong,
            color: ig.text,
            border: `1px solid ${ig.rule}`,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: ig.raised,
            border: `1px solid ${ig.rule}`,
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: ig.accent,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            '&.Mui-selected': {
              color: ig.accent,
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: ig.raised,
              fontWeight: 600,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: ig.rule,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: ig.raisedStrong,
          },
        },
      },
    },
  });
}

// The dark theme stays the default export for tests and code outside the provider.
const theme = buildTheme('dark');

export default theme;
